function clampValue(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// Pinch ceiling; a plain desktop click caps lower - see getClickMaxScale().
const ZOOM_MAX_SCALE = 3;
const DESKTOP_CLICK_MAX_SCALE = 2;
// Under this much movement a touch is still a tap (hand tremor).
const DRAG_THRESHOLD = 10;
// Faster than 1:1, so a smaller drag covers more of the image.
const DRAG_SPEED = 1.6;
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
// Long enough that a cursor crossing the gallery fetches nothing.
const ZOOM_WARM_HOVER_DELAY = 200;
// Matches .zoom-modal's opacity transition in component-product-zoom-modal.css.
const ZOOM_CLOSE_FADE = 300;

class ProductZoomModal extends HTMLElement {
  connectedCallback() {
    this.closeButton = this.querySelector('.zoom-modal__close');
    this.thumbs = [...this.querySelectorAll('.zoom-modal__thumb')];
    this.slidesContainer = this.querySelector('.zoom-modal__slides');
    this.slides = [...this.querySelectorAll('.zoom-modal__slide')];
    this.activeIndex = 0;
    this.warmed = new Set();

    this.closeButton.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.selectSlide(Number(thumb.dataset.index)));
      // Desktop only requests a thumb's image when chosen, so warm it on hover.
      thumb.addEventListener('pointerenter', () => this.warmSlide(Number(thumb.dataset.index)));
    });

    document.querySelectorAll(`[data-zoom-trigger][data-section="${this.dataset.section}"]`).forEach((trigger) => {
      const index = Number(trigger.dataset.index);
      trigger.addEventListener('click', () => this.open(index));

      // Mouse only: on a phone a press is as often the start of a swipe as a tap.
      trigger.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.warmTimer);
        this.warmTimer = setTimeout(() => {
          if (typeof prefetchUnwelcome === 'function' && prefetchUnwelcome()) return;
          this.warmSlide(index);
        }, ZOOM_WARM_HOVER_DELAY);
      });
      trigger.addEventListener('pointerleave', () => clearTimeout(this.warmTimer));
      trigger.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') this.warmSlide(index);
      });
    });

    // Scrolling the stacked list (mobile) means another image is wanted, so let go of any zoom.
    this.slidesContainer?.addEventListener('scroll', () => {
      const zoomedSlide = this.slides.find((slide) => slide.zoomScale > 1);
      if (!zoomedSlide) return;
      const container = zoomedSlide.querySelector('.zoom-modal__zoom-container');
      const img = zoomedSlide.querySelector('img');
      if (container && img) this.zoomOut(zoomedSlide, container, img);
    });

    this.slides.forEach((slide) => this.bindZoom(slide));
  }

  // A click has no "how far in" gesture, so desktop caps lower; keyed on width like the theme's breakpoints.
  getClickMaxScale() {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches ? ZOOM_MAX_SCALE : DESKTOP_CLICK_MAX_SCALE;
  }

  // Mobile overrides [hidden] in CSS so slides stack; only the CSS differs per breakpoint.
  selectSlide(index) {
    if (!this.slides[index]) return;
    this.resetZoom(this.slides[this.activeIndex]);
    this.slides.forEach((slide, i) => slide.toggleAttribute('hidden', i !== index));
    this.activeIndex = index;
    this.syncActiveThumb(index);
  }

  syncActiveThumb(index) {
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }

  // Same URL as the slide (no srcset), so the modal's lazy <img> is an exact cache hit.
  warmSlide(index) {
    const img = this.slides[index]?.querySelector('img');
    if (!img || img.complete || this.warmed.has(index)) return;
    this.warmed.add(index);
    const warm = new Image();
    warm.fetchPriority = 'high';
    warm.src = img.getAttribute('src');
  }

  open(index) {
    clearTimeout(this.closingTimer);
    this.classList.remove('is-closing');

    // The opened slide first: on mobile the stacked list starts its neighbours at once.
    const img = this.slides[index]?.querySelector('img');
    if (img) {
      img.fetchPriority = 'high';
      img.loading = 'eager';
    }

    this.opener = document.activeElement;
    this.removeAttribute('inert');
    document.body.classList.add('overflow-hidden');
    this.selectSlide(index);
    this.slides[index]?.scrollIntoView({ block: 'start' });
    trapFocus(this, this.closeButton);

    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) this.loadInOrder(index);
  }

  // One at a time, nearest the opened slide first, rather than several originals sharing bandwidth.
  async loadInOrder(index) {
    if (this.loadingInOrder) return;
    if (typeof prefetchUnwelcome === 'function' && prefetchUnwelcome()) return;
    this.loadingInOrder = true;

    const ordered = this.slides
      .map((slide, i) => ({ img: slide.querySelector('img'), distance: Math.abs(i - index) }))
      .filter(({ img }) => img)
      .sort((a, b) => a.distance - b.distance);

    for (const { img } of ordered) {
      if (img.complete) continue;
      img.loading = 'eager';
      await new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }
  }

  close() {
    // Keeps content in layout through the fade out; see .zoom-modal[inert] in the stylesheet.
    this.classList.add('is-closing');
    clearTimeout(this.closingTimer);
    this.closingTimer = setTimeout(() => this.classList.remove('is-closing'), ZOOM_CLOSE_FADE);

    this.setAttribute('inert', '');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
    this.slides.forEach((slide) => this.resetZoom(slide));
  }

  // --- Per-slide zoom ---
  // Native scroll at scale 1, transforms once zoomed; not Swiper's Zoom - see decisions.md: PDP zoom.
  bindZoom(slide) {
    const container = slide.querySelector('.zoom-modal__zoom-container');
    const img = slide.querySelector('img');
    if (!container || !img) return;

    slide.zoomScale = 1;
    slide.scrollOrigin = 0;
    slide.usingTouch = false;

    // Synthetic mouse events follow a real touch - see known-issues.md: Touch input.
    slide.addEventListener('touchstart', () => { slide.usingTouch = true; }, { capture: true, passive: true });

    container.addEventListener('click', (event) => {
      // A drag's release also fires this click; only the drag flag tells it from a tap.
      if (slide.dragMoved) {
        slide.dragMoved = false;
        return;
      }
      if (slide.zoomScale > 1) {
        this.zoomOut(slide, container, img);
      } else {
        this.zoomIn(slide, container, img, event.clientX, event.clientY, this.getClickMaxScale());
      }
    });

    slide.addEventListener('mousemove', (event) => {
      if (slide.usingTouch || slide.zoomScale <= 1) return; // touch has its own drag handling below; native scroll handles panning at scale 1
      const rect = slide.getBoundingClientRect();
      const fractionX = clampValue((event.clientX - rect.left) / rect.width, 0, 1);
      const fractionY = this.getHoverFractionY(slide, event.clientY - rect.top, rect.height);
      this.applyTransform(slide, container, img, fractionX, fractionY, true);
    });

    this.bindPinch(slide, container, img);
  }

  // Remapped through the click's landing point, so the first move does not jump and the whole image stays reachable.
  getHoverFractionY(slide, cy, viewportHeight) {
    const clickCy = slide.clickCy;
    const landing = slide.landingFractionY;
    if (cy === clickCy) return landing; // exactly where zoomIn() landed - must match it exactly, not just approach it
    if (cy < clickCy) {
      return clickCy > 0 ? clampValue(landing * (cy / clickCy), 0, 1) : landing;
    }
    const remaining = viewportHeight - clickCy;
    return remaining > 0 ? clampValue(landing + (1 - landing) * ((cy - clickCy) / remaining), 0, 1) : landing;
  }

  // Scroll-aware landing; the transform is set first because scrollTop cannot animate.
  zoomIn(slide, container, img, clientX, clientY, scale) {
    const rect = slide.getBoundingClientRect();
    const scrollOrigin = slide.scrollTop;
    const clickCy = clientY - rect.top;
    const fractionX = clampValue((clientX - rect.left) / rect.width, 0, 1);
    const fractionY = clampValue((scrollOrigin + clickCy) / img.offsetHeight, 0, 1);

    slide.scrollOrigin = scrollOrigin;
    slide.clickCy = clickCy;
    slide.landingFractionY = fractionY;
    slide.zoomScale = scale;
    slide.classList.add('is-zoomed');

    if (scrollOrigin) {
      container.style.transitionDuration = '0s';
      container.style.transform = `translate3d(0px, ${-scrollOrigin}px, 0)`;
      img.style.transitionDuration = '0s';
      img.style.transform = 'scale(1)';
      void container.offsetHeight; // force a reflow so the lines above actually commit first
    }
    slide.scrollTop = 0;

    this.applyTransform(slide, container, img, fractionX, fractionY, false);
  }

  // Land where the view currently is (after any pan), not at the original click point.
  zoomOut(slide, container, img) {
    if (slide.zoomScale <= 1) return; // avoids a double finalize if the outer scroll listener also calls this
    const scale = slide.zoomScale;
    const rect = slide.getBoundingClientRect();
    const naturalHeight = img.offsetHeight;
    const centerOffsetY = (rect.height - naturalHeight) / 2;
    const translateY = slide.translateY ?? centerOffsetY;
    const maxScrollTop = Math.max(0, naturalHeight - rect.height);
    const scrollOrigin = clampValue((naturalHeight * (1 - 1 / scale)) / 2 - translateY / scale, 0, maxScrollTop);

    slide.zoomScale = 1;

    container.style.transitionDuration = '300ms';
    container.style.transform = scrollOrigin ? `translate3d(0px, ${-scrollOrigin}px, 0)` : '';
    img.style.transitionDuration = '300ms';
    img.style.transform = '';

    const finalize = () => {
      slide.classList.remove('is-zoomed');
      slide.scrollTop = scrollOrigin;
      slide.scrollOrigin = 0;
      container.style.transitionDuration = '0s';
      container.style.transform = '';
      void container.offsetHeight;
      container.style.transitionDuration = '';
    };

    container.addEventListener('transitionend', finalize, { once: true });
  }

  // A drag tracks its delta, a pinch its midpoint - see known-issues.md: Touch input.
  bindPinch(slide, container, img) {
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    let pinchStartedUnzoomed = false;
    let pinchTouchIdA = null;
    let pinchTouchIdB = null;
    let lastPinchMidX = 0;
    let lastPinchMidY = 0;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let dragStartX = 0;
    let dragStartY = 0;

    // By identifier: TouchList order is not stable across events.
    const findTouch = (touches, id) => {
      for (let i = 0; i < touches.length; i++) {
        if (touches[i].identifier === id) return touches[i];
      }
      return null;
    };
    const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midpoint = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

    slide.addEventListener('touchstart', (event) => {
      if (event.touches.length === 2) {
        pinchTouchIdA = event.touches[0].identifier;
        pinchTouchIdB = event.touches[1].identifier;
        pinchStartDistance = distance(event.touches[0], event.touches[1]);
        pinchStartScale = slide.zoomScale;
        pinchStartedUnzoomed = pinchStartScale <= 1;
        // Anchor only, so placing a second finger does not move anything.
        const mid = midpoint(event.touches[0], event.touches[1]);
        lastPinchMidX = mid.x;
        lastPinchMidY = mid.y;
        return;
      }
      if (event.touches.length === 1) {
        // Anchor only, so a tap alone never moves the image.
        lastTouchX = event.touches[0].clientX;
        lastTouchY = event.touches[0].clientY;
        dragStartX = lastTouchX;
        dragStartY = lastTouchY;
        slide.dragMoved = false;
      }
    });

    slide.addEventListener('touchmove', (event) => {
      if (event.touches.length === 2 && pinchStartDistance) {
        const touchA = findTouch(event.touches, pinchTouchIdA);
        const touchB = findTouch(event.touches, pinchTouchIdB);
        // A different finger took one of the two slots: not the same pinch.
        if (!touchA || !touchB) return;

        event.preventDefault();
        const ratio = distance(touchA, touchB) / pinchStartDistance;
        const mid = midpoint(touchA, touchB);
        const newScale = clampValue(pinchStartScale * ratio, 1, ZOOM_MAX_SCALE);

        // Same scroll correction as zoomIn(); the one place an absolute position is used.
        if (pinchStartedUnzoomed) {
          pinchStartedUnzoomed = false;
          this.zoomIn(slide, container, img, mid.x, mid.y, newScale);
        } else {
          // Pan by the midpoint's delta, so a joining finger does not recenter the image.
          slide.zoomScale = newScale;
          this.panByPixels(
            slide,
            container,
            img,
            (mid.x - lastPinchMidX) * DRAG_SPEED,
            (mid.y - lastPinchMidY) * DRAG_SPEED
          );
        }
        lastPinchMidX = mid.x;
        lastPinchMidY = mid.y;
        return;
      }

      if (event.touches.length === 1 && slide.zoomScale > 1) {
        const touch = event.touches[0];

        // On the first touchmove, or the browser commits to pull-to-refresh - see known-issues.md: Touch input.
        event.preventDefault();

        if (pinchStartDistance) {
          // Restart the anchor, or the pre-pinch position reads as a huge delta.
          pinchStartDistance = 0;
          lastTouchX = touch.clientX;
          lastTouchY = touch.clientY;
          slide.dragMoved = true; // already mid-gesture - don't re-require crossing the tap threshold
          return;
        }

        // Below DRAG_THRESHOLD it is still a tap, left to the click's tap-to-zoom.
        if (!slide.dragMoved) {
          const totalDistance = Math.hypot(touch.clientX - dragStartX, touch.clientY - dragStartY);
          if (totalDistance < DRAG_THRESHOLD) return;
          slide.dragMoved = true;
        }

        this.panByPixels(
          slide,
          container,
          img,
          (touch.clientX - lastTouchX) * DRAG_SPEED,
          (touch.clientY - lastTouchY) * DRAG_SPEED
        );
        lastTouchX = touch.clientX;
        lastTouchY = touch.clientY;
      }
    });

    slide.addEventListener('touchend', (event) => {
      if (event.touches.length > 0) return;
      // touchend fires for any touch, so this pinch cleanup is scoped by pinchStartDistance.
      const wasPinching = pinchStartDistance > 0;
      pinchStartDistance = 0;
      pinchTouchIdA = null;
      pinchTouchIdB = null;
      if (wasPinching && slide.zoomScale <= 1.05) this.zoomOut(slide, container, img);
    });
  }

  // Keeps (fractionX, fractionY) of the slide's box fixed; the pan range is centred on the image's natural size.
  applyTransform(slide, container, img, fractionX, fractionY, instant) {
    const scale = slide.zoomScale;
    const rect = slide.getBoundingClientRect();
    const naturalWidth = img.offsetWidth;
    const naturalHeight = img.offsetHeight;
    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;

    const centerOffsetX = (rect.width - naturalWidth) / 2;
    const centerOffsetY = (rect.height - naturalHeight) / 2;
    const maxX = Math.max(0, (scaledWidth - rect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - rect.height) / 2);

    const translateX = centerOffsetX + maxX * (1 - 2 * fractionX);
    const translateY = centerOffsetY + maxY * (1 - 2 * fractionY);

    this.setTranslate(slide, container, img, translateX, translateY, instant);
  }

  // A drag adds a clamped pixel delta, not a fraction from the touch position.
  panByPixels(slide, container, img, deltaX, deltaY) {
    const rect = slide.getBoundingClientRect();
    const scale = slide.zoomScale;
    const naturalWidth = img.offsetWidth;
    const naturalHeight = img.offsetHeight;

    const centerOffsetX = (rect.width - naturalWidth) / 2;
    const centerOffsetY = (rect.height - naturalHeight) / 2;
    const maxX = Math.max(0, (naturalWidth * scale - rect.width) / 2);
    const maxY = Math.max(0, (naturalHeight * scale - rect.height) / 2);

    const currentX = slide.translateX ?? centerOffsetX;
    const currentY = slide.translateY ?? centerOffsetY;
    const translateX = clampValue(currentX + deltaX, centerOffsetX - maxX, centerOffsetX + maxX);
    const translateY = clampValue(currentY + deltaY, centerOffsetY - maxY, centerOffsetY + maxY);

    this.setTranslate(slide, container, img, translateX, translateY, true);
  }

  setTranslate(slide, container, img, translateX, translateY, instant) {
    slide.translateX = translateX;
    slide.translateY = translateY;

    // Container and img transform separately, so both need the same transition or they drift.
    const transitionDuration = instant ? '0s' : '300ms';
    container.style.transitionDuration = transitionDuration;
    container.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
    img.style.transitionDuration = transitionDuration;
    img.style.transform = `scale(${slide.zoomScale})`;
  }

  resetZoom(slide) {
    const container = slide.querySelector('.zoom-modal__zoom-container');
    const img = slide.querySelector('img');
    slide.zoomScale = 1;
    slide.scrollOrigin = 0;
    slide.translateX = undefined;
    slide.translateY = undefined;
    slide.classList.remove('is-zoomed');
    if (container) {
      container.style.transitionDuration = '';
      container.style.transform = '';
    }
    if (img) {
      img.style.transitionDuration = '';
      img.style.transform = '';
    }
  }
}
customElements.define('product-zoom-modal', ProductZoomModal);
